<script setup lang="ts">
import { useMemberStats } from '@/composables/poolMetrics/usePoolMetrics'
import { TRIAL_DURATION_DAYS, TRIAL_MEMBERSHIP_CAP } from '#shared/constants/foundation'
import { LANDING_TRIAL } from '@/content/landing'

const { data: stats } = useMemberStats({ lazy: true })

// `null` until the member counts load: an empty strip would claim every spot is free.
const taken = computed(() => stats.value == null ? null : (stats.value.totalByTier?.trial ?? 0))
const remaining = computed(() => taken.value == null ? null : Math.max(TRIAL_MEMBERSHIP_CAP - taken.value, 0))
const soldOut = computed(() => remaining.value === 0)

const remainingLabel = computed(() => remaining.value == null
  ? null
  : LANDING_TRIAL.remaining
      .replace('{remaining}', String(remaining.value))
      .replace('{cap}', String(TRIAL_MEMBERSHIP_CAP)),
)

// Open spots light up, so a trial with room reads as full of chances rather than empty.
const pips = computed(() =>
  Array.from({ length: TRIAL_MEMBERSHIP_CAP }, (_, i) => ({
    number: i + 1,
    open: taken.value != null && i >= taken.value,
  })),
)
</script>

<template>
  <div id="trial" class="trial-strip scroll-mt-24">
    <div class="trial-strip__tape" aria-hidden="true" />

    <div class="trial-strip__body">
      <!-- One tick per day of the trial; they light up around the dial as the section arrives. -->
      <div class="trial-dial" aria-hidden="true">
        <svg viewBox="0 0 120 120" class="trial-dial__svg">
          <defs>
            <mask id="trial-dial-sweep">
              <circle class="trial-dial__sweep" cx="60" cy="60" r="53" pathLength="100" />
            </mask>
          </defs>
          <circle class="trial-dial__ticks" cx="60" cy="60" r="53" :pathLength="TRIAL_DURATION_DAYS" />
          <circle
            class="trial-dial__ticks trial-dial__ticks--lit"
            cx="60"
            cy="60"
            r="53"
            :pathLength="TRIAL_DURATION_DAYS"
            mask="url(#trial-dial-sweep)"
          />
          <circle class="trial-dial__ring" cx="60" cy="60" r="43" />
        </svg>
        <span class="trial-dial__value font-mono tabular-nums">{{ LANDING_TRIAL.dial.value }}</span>
        <UIText variant="label" as="span" class="trial-dial__caption">
          {{ LANDING_TRIAL.dial.caption }}
        </UIText>
      </div>

      <UISection
        :level="2"
        band="none"
        :kicker="LANDING_TRIAL.kicker"
        :title="LANDING_TRIAL.heading"
        :subtitle="soldOut ? LANDING_TRIAL.soldOut : LANDING_TRIAL.body"
        class="min-w-0"
      >
        <template v-if="!soldOut" #details>
          <ul class="flex flex-wrap gap-2">
            <li v-for="perk in LANDING_TRIAL.perks" :key="perk" class="trial-perk">
              <UIIcon name="checkmark-filled" size="sm" class="text-accent" />
              {{ perk }}
            </li>
          </ul>
        </template>
      </UISection>

      <div class="trial-strip__action">
        <!-- The count below says the same in words, so the pips are decoration. -->
        <div
          class="trial-strip__pips"
          :class="{ 'is-loading': remaining == null }"
          aria-hidden="true"
        >
          <span
            v-for="pip in pips"
            :key="pip.number"
            class="trial-pip"
            :class="{ 'is-open': pip.open }"
          />
        </div>

        <p v-if="remainingLabel != null" class="trial-strip__count">
          {{ remainingLabel }}
        </p>
        <span v-else class="trial-strip__count-placeholder" aria-hidden="true" />

        <UIButton
          :variant="soldOut ? 'secondary' : 'cta'"
          size="md"
          :to="LANDING_TRIAL.ctaHref"
          :prefetch="false"
        >
          {{ soldOut ? LANDING_TRIAL.soldOutCta : LANDING_TRIAL.cta }}
        </UIButton>
        <p v-if="!soldOut" class="text-xs text-ink-faint">
          {{ LANDING_TRIAL.upgradeNote }}
        </p>
      </div>
    </div>
  </div>
</template>
